import { FC, useMemo } from 'react';
import { DashifyHighcharts } from '../DashifyHighcharts';
import { css, format, MAGNETIC_TOKENS } from '@appd/uik';
import { ArrayPoint, ChartType, handleOrphanDataPoints, transformArrayPoints } from '.';
import { defaultsDeep } from 'lodash';

export interface SparklineCellRendererProps {
  data: ArrayPoint[];
  showSummaryValue?: boolean;
  summaryValue?: number;
  summaryValueColumn?: string;
  unit?: string;
  highchartsConfig?: Record<string, unknown>;
}

const { COLORS, SPACING } = MAGNETIC_TOKENS;

const HEIGHT = 30;
const EMPTY_STATE = '—';

const STYLES = {
  CONTAINER: css.Styles({
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    height: HEIGHT,
    alignItems: 'center',
    gap: SPACING[1],
  }),
  CHART_CONTAINER: css.Styles({
    ...{
      flex: 1,
      width: '100%',
    },
  }),
  SUMMARY_VALUE_CONTAINER: css.Styles({
    display: 'flex',
    alignItems: 'center',
  }),
  SUMMARY_VALUE: css.Styles({
    fontSize: '12px',
    lineHeight: '18px',
  }),
  SUMMARY_VALUE_UNIT: css.Styles({
    marginLeft: SPACING[1],
    fontSize: '12px',
    lineHeight: '18px',
  }),
};

const DEFAULT_HIGHCHARTS_CONFIG = {
  chart: {
    type: ChartType.LINE,
    spacing: [0, 0, 0, 0],
    styledMode: false,
  },
  plotOptions: {
    column: {
      borderRadius: 0,
      borderWidth: 0,
    },
    series: {
      color: COLORS.ACCENT_A,
      crisp: false, // setting crisp to false makes columns more distinguishable when densely packed
      marker: {
        enabled: false,
        radius: 3,
      },
      maxPointWidth: 3, // maximum width for a column or bar (otherwise width may become too wide when there are few points)
      minPointLength: 3, // minimum height for a column or width for a bar (by default, 0 values are not shown)
    },
  },
  tooltip: {
    headerFormat: '',
    pointFormat: '<b>{point.y}</b>',
    valueDecimals: 2,
    style: {
      fontSize: '12px',
      lineHeight: '18px',
    },
    borderRadius: 6,
    borderWidth: 2,
  },
  yAxis: {
    visible: false,
  },
  xAxis: {
    type: 'datetime',
    visible: false,
  },
};

// This component renders a sparkline (small graphic that represents data and doesn't show axes or coordinates)
// Currently following https://www.figma.com/file/oVZWatImEIbl1c8sjdGxi0/%F0%9F%A7%B2--Magnetic-Design-Library?node-id=5171%3A45911&mode=dev
// TODO: Update to https://www.figma.com/file/cfkXwPQvA6QxbxnkZqakCk/Magnetic-Charts?node-id=2788%3A25951&mode=dev after SingleValue React component is ready
// TODO: Write tests
export const SparklineCellRenderer: FC<SparklineCellRendererProps> = ({
  data,
  summaryValue,
  unit,
  highchartsConfig,
  showSummaryValue = true,
}) => {
  const seriesData = useMemo(() => handleOrphanDataPoints(transformArrayPoints({ data })), [data]);
  // use last value as default summary value to display
  const displayValue = summaryValue ? summaryValue : data && data.length > 0 ? data[data.length - 1][1] : null;
  const chartOptions = highchartsConfig
    ? defaultsDeep({}, highchartsConfig, DEFAULT_HIGHCHARTS_CONFIG)
    : DEFAULT_HIGHCHARTS_CONFIG;

  return (
    <div style={STYLES.CONTAINER}>
      <div style={STYLES.CHART_CONTAINER}>
        {data && data.length > 0 ? (
          <DashifyHighcharts height={HEIGHT} series={seriesData} {...chartOptions} />
        ) : (
          <p>{EMPTY_STATE}</p>
        )}
      </div>
      {showSummaryValue && (
        <div style={STYLES.SUMMARY_VALUE_CONTAINER}>
          <div style={STYLES.SUMMARY_VALUE}>{displayValue ? format.number.short(displayValue) : EMPTY_STATE}</div>
          <p style={STYLES.SUMMARY_VALUE_UNIT}>{unit}</p>
        </div>
      )}
    </div>
  );
};
